import { useState, type ComponentType, type ReactNode } from 'react';
import { View, Text, StyleSheet, Pressable, Platform, useWindowDimensions } from 'react-native';
import WebScrollView from '../../components/WebScrollView';
import { useNavigate } from 'react-router-dom';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import {
  Users,
  Home as HomeIcon,
  Menu as MenuIcon,
  MessageCircle,
  ClipboardList,
  DollarSign,
  Building,
  CalendarClock,
  Briefcase,
  Wrench,
  TrendingUp,
  Receipt,
  Upload,
  Sparkles,
  Settings,
  Shield,
  X,
  Calendar,
  Gift,
  CheckSquare,
  Send,
  UserCheck,
  KeyRound,
  ExternalLink,
} from '../../lib/icons';
import { openExternalLink } from '../../lib/platform';
import { shadows } from '../../lib/styles';

/** Full URL for the admin console on the deployed web app. */
const WEB_ADMIN_URL = 'https://app.honeydone.com/admin';

const usePlatformSafeAreaInsets = () => {
  if (Platform.OS === 'web') {
    return { top: 0, right: 0, bottom: 0, left: 0 };
  }
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const insets = useSafeAreaInsets();
  return insets;
};

export type AdminTabValue =
  | 'myday'
  | 'tasks'
  | 'ai'
  | 'configuration'
  | 'users'
  | 'pipeline'
  | 'requests'
  | 'visits'
  | 'providers'
  | 'quotes'
  | 'import'
  | 'team'
  | 'agents'
  | 'reports'
  | 'conversations'
  | 'referrals'
  | 'walkthrough'
  | 'homes'
  | 'expenses'
  | 'announcements'
  | 'api-keys'
  | 'debug';

export type ReportsViewValue = 'overview' | 'performance' | 'standards';

export const REPORTS_VIEW_OPTIONS: { value: ReportsViewValue; label: string }[] = [
  { value: 'overview', label: 'Overview' },
  { value: 'performance', label: 'Performance' },
  { value: 'standards', label: 'Standards & QA' },
];

export const ADMIN_TAB_SUB_NAV: Partial<
  Record<AdminTabValue, { value: string; label: string }[]>
> = {
  reports: REPORTS_VIEW_OPTIONS,
};

export const ADMIN_TAB_OPTIONS: { value: AdminTabValue; label: string }[] = [
  { value: 'myday', label: 'My Day' },
  { value: 'tasks', label: 'Tasks' },
  { value: 'conversations', label: 'Messages' },
  { value: 'requests', label: 'Service Requests' },
  { value: 'users', label: 'Users' },
  { value: 'visits', label: 'Visits' },
  { value: 'homes', label: 'Homes' },
  { value: 'walkthrough', label: 'Walkthrough Availability' },
  { value: 'providers', label: 'Service Providers' },
  { value: 'pipeline', label: 'Pipeline' },
  { value: 'reports', label: 'Reports' },
  { value: 'referrals', label: 'Referrals' },
  { value: 'expenses', label: 'Expenses' },
  { value: 'announcements', label: 'Announcements' },
  { value: 'import', label: 'Property Import' },
  { value: 'quotes', label: 'Market Research Quotes' },
  { value: 'team', label: 'Team' },
  { value: 'agents', label: 'Real Estate Agents' },
  { value: 'ai', label: 'AI Assistant' },
  { value: 'api-keys', label: 'API Keys' },
  { value: 'debug', label: 'Debug Tools' },
  { value: 'configuration', label: 'Configuration' },
];

const tabIcons: Partial<Record<AdminTabValue, ComponentType<{ size?: number; color?: string }>>> = {
  myday: Calendar,
  tasks: CheckSquare,
  configuration: Settings,
  conversations: MessageCircle,
  requests: ClipboardList,
  quotes: DollarSign,
  users: Users,
  visits: CalendarClock,
  homes: Building,
  walkthrough: CalendarClock,
  team: Briefcase,
  agents: UserCheck,
  providers: Wrench,
  pipeline: TrendingUp,
  reports: TrendingUp,
  referrals: Gift,
  expenses: Receipt,
  announcements: Send,
  import: Upload,
  ai: Sparkles,
  'api-keys': KeyRound,
  debug: Settings,
};

const SIDEBAR_WIDTH = 228;

type AdminLayoutProps = {
  children: ReactNode;
  /** Which admin tab is highlighted; `null` on standalone pages (e.g. user profile). */
  activeTab: AdminTabValue | null;
  visibleTabs?: AdminTabValue[];
  /** Currently selected sub-view (e.g. for the Reports tab). */
  activeSubView?: string | null;
  /** Called when the user picks a sub-nav item. */
  onSubViewChange?: (view: string) => void;
};

const AdminLayout = ({
  children,
  activeTab,
  visibleTabs,
  activeSubView,
  onSubViewChange,
}: AdminLayoutProps) => {
  const navigate = useNavigate();
  const insets = usePlatformSafeAreaInsets();
  const [navOpen, setNavOpen] = useState(false);
  const { width: windowWidth } = useWindowDimensions();
  const isDesktop = windowWidth >= 768;

  const goToTab = (tab: AdminTabValue) => {
    navigate(`/admin?tab=${tab}`);
    if (!isDesktop) setNavOpen(false);
  };

  const handleSubNavPress = (view: string) => {
    if (onSubViewChange) {
      onSubViewChange(view);
    }
    if (!isDesktop) setNavOpen(false);
  };

  const handleOpenOnWeb = () => {
    // `inAppBrowser: false` routes through Linking.openURL so the link opens in
    // the user's default system browser instead of an in-app web view.
    openExternalLink(WEB_ADMIN_URL, { inAppBrowser: false });
    if (!isDesktop) setNavOpen(false);
  };

  const tabsToRender = visibleTabs
    ? ADMIN_TAB_OPTIONS.filter((tab) => visibleTabs.includes(tab.value))
    : ADMIN_TAB_OPTIONS;

  return (
    <View style={styles.outerContainer}>
      {!isDesktop && (
        <View style={[styles.mobileHeader, { paddingTop: Platform.OS !== 'web' ? insets.top : 0 }]}>
          <Pressable style={styles.hamburgerBtn} onPress={() => setNavOpen((prev) => !prev)}>
            {navOpen ? <X size={22} color="#f1f5f9" /> : <MenuIcon size={22} color="#f1f5f9" />}
          </Pressable>
          <Text style={styles.mobileHeaderTitle}>Admin Portal</Text>
          <Pressable style={styles.mobileHomeBtn} onPress={() => navigate('/dashboard')}>
            <HomeIcon size={20} color="#94a3b8" />
          </Pressable>
        </View>
      )}

      <View style={styles.bodyLayout}>
        {!isDesktop && navOpen && (
          <Pressable style={styles.overlay} onPress={() => setNavOpen(false)} />
        )}

        {(isDesktop || navOpen) && (
          <View style={[styles.sidebar, !isDesktop && styles.sidebarOverlay]}>
            {isDesktop && (
              <View style={[styles.desktopSidebarTop, { paddingTop: Platform.OS !== 'web' ? insets.top : 20 }]}>
                <View style={styles.desktopSidebarBrand}>
                  <Shield size={18} color="#60a5fa" />
                  <Text style={styles.desktopSidebarTitle}>Admin Portal</Text>
                </View>
                <Pressable style={styles.goHomeBtn} onPress={() => navigate('/dashboard')}>
                  <HomeIcon size={13} color="#64748b" />
                  <Text style={styles.goHomeBtnText}>Go Home</Text>
                </Pressable>
              </View>
            )}

            <WebScrollView style={styles.navScroll}>
              {tabsToRender.map((tab) => {
                const Icon = tabIcons[tab.value];
                const isActive = activeTab === tab.value;
                const subNavItems = ADMIN_TAB_SUB_NAV[tab.value];
                return (
                  <View key={tab.value}>
                    <Pressable
                      style={[styles.navItem, isActive && styles.navItemActive]}
                      onPress={() => goToTab(tab.value)}
                    >
                      <View
                        style={[styles.navItemAccent, isActive && styles.navItemAccentActive]}
                      />
                      {Icon && (
                        <Icon size={17} color={isActive ? '#60a5fa' : '#64748b'} />
                      )}
                      <Text style={[styles.navLabel, isActive && styles.navLabelActive]}>
                        {tab.label}
                      </Text>
                    </Pressable>

                    {isActive && subNavItems && subNavItems.length > 0 && (
                      <View style={styles.subNavGroup}>
                        {subNavItems.map((sub) => {
                          const isSubActive = activeSubView === sub.value;
                          return (
                            <Pressable
                              key={sub.value}
                              style={[styles.subNavItem, isSubActive && styles.subNavItemActive]}
                              onPress={() => handleSubNavPress(sub.value)}
                            >
                              <View
                                style={[
                                  styles.subNavDot,
                                  isSubActive && styles.subNavDotActive,
                                ]}
                              />
                              <Text
                                style={[
                                  styles.subNavLabel,
                                  isSubActive && styles.subNavLabelActive,
                                ]}
                              >
                                {sub.label}
                              </Text>
                            </Pressable>
                          );
                        })}
                      </View>
                    )}
                  </View>
                );
              })}
            </WebScrollView>

            {Platform.OS !== 'web' && (
              <View style={styles.nativeFooter}>
                <Pressable style={styles.openOnWebItem} onPress={handleOpenOnWeb}>
                  <ExternalLink size={17} color="#60a5fa" />
                  <Text style={styles.openOnWebLabel}>Open Full Admin on Web</Text>
                </Pressable>
                <Text style={styles.openOnWebHint}>
                  Some admin tools are only available in your browser.
                </Text>
              </View>
            )}
          </View>
        )}

        <View style={styles.content}>{children}</View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  outerContainer: {
    flex: 1,
    minHeight: 0,
    flexDirection: 'column',
    backgroundColor: '#f8fafc',
    ...(Platform.OS === 'web' && {
      height: '100%',
    }),
  },
  mobileHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingBottom: 12,
    paddingTop: 12,
    backgroundColor: '#1e293b',
    borderBottomWidth: 1,
    borderBottomColor: '#334155',
  },
  mobileHeaderTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: '#f1f5f9',
    letterSpacing: 0.3,
  },
  hamburgerBtn: {
    padding: 4,
  },
  mobileHomeBtn: {
    padding: 4,
  },
  bodyLayout: {
    flex: 1,
    minHeight: 0,
    flexDirection: 'row',
    position: 'relative',
  },
  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.45)',
    zIndex: 10,
  },
  sidebar: {
    width: SIDEBAR_WIDTH,
    backgroundColor: '#1e293b',
    flexDirection: 'column',
    borderRightWidth: 1,
    borderRightColor: '#334155',
  },
  sidebarOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    bottom: 0,
    zIndex: 20,
    ...shadows.xl,
  },
  desktopSidebarTop: {
    paddingHorizontal: 20,
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#334155',
    gap: 12,
  },
  desktopSidebarBrand: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  desktopSidebarTitle: {
    fontSize: 15,
    fontWeight: '700',
    color: '#f1f5f9',
    letterSpacing: 0.4,
  },
  goHomeBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    alignSelf: 'flex-start',
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 6,
    backgroundColor: '#334155',
  },
  goHomeBtnText: {
    fontSize: 12,
    color: '#94a3b8',
    fontWeight: '500',
  },
  navScroll: {
    flex: 1,
    paddingVertical: 8,
  },
  navItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingHorizontal: 16,
    paddingVertical: 10,
    position: 'relative',
  },
  navItemActive: {
    backgroundColor: '#334155',
  },
  navItemAccent: {
    position: 'absolute',
    left: 0,
    top: 6,
    bottom: 6,
    width: 3,
    borderRadius: 2,
    backgroundColor: 'transparent',
  },
  navItemAccentActive: {
    backgroundColor: '#60a5fa',
  },
  navLabel: {
    fontSize: 13,
    color: '#94a3b8',
    fontWeight: '500',
    flex: 1,
  },
  navLabelActive: {
    color: '#f1f5f9',
    fontWeight: '600',
  },
  subNavGroup: {
    paddingLeft: 28,
    paddingRight: 8,
    paddingVertical: 4,
    gap: 2,
  },
  subNavItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingHorizontal: 12,
    paddingVertical: 7,
    borderRadius: 6,
  },
  subNavItemActive: {
    backgroundColor: '#243349',
  },
  subNavDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: '#475569',
  },
  subNavDotActive: {
    backgroundColor: '#60a5fa',
  },
  subNavLabel: {
    fontSize: 12,
    color: '#94a3b8',
    fontWeight: '500',
    flex: 1,
  },
  subNavLabelActive: {
    color: '#e2e8f0',
    fontWeight: '600',
  },
  content: {
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
  },
  nativeFooter: {
    paddingHorizontal: 16,
    paddingVertical: 14,
    borderTopWidth: 1,
    borderTopColor: '#334155',
    gap: 6,
    backgroundColor: '#1e293b',
  },
  openOnWebItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderRadius: 8,
    backgroundColor: '#243349',
  },
  openOnWebLabel: {
    fontSize: 13,
    fontWeight: '600',
    color: '#e2e8f0',
    flex: 1,
  },
  openOnWebHint: {
    fontSize: 11,
    color: '#64748b',
    paddingHorizontal: 4,
    lineHeight: 15,
  },
});

export default AdminLayout;
